import * as THREE from 'three';
import { STAR_RADIUS } from './constants';
import { NeutronStarParams } from './types';

/**
 * NeutronStar manages the star mesh, spin axis orientation, and per-frame rotation.
 *
 * Rotation is computed as: rotationPhase += spinSpeed * 2π * deltaTime
 * The spin axis quaternion is derived from the inclination angle (tilt from Y-axis around X-axis).
 * A rotation quaternion is then applied around that tilted axis each frame.
 */
export class NeutronStar {
  public mesh: THREE.Mesh;
  public spinAxis: THREE.Vector3;

  private rotationPhase: number = 0;
  private spinSpeed: number;
  private inclinationAngle: number; // stored in radians internally
  private material: THREE.MeshStandardMaterial;

  constructor(params?: Partial<NeutronStarParams>, geometryOptions?: { widthSegments?: number; heightSegments?: number }) {
    const defaultColor = new THREE.Color('#B4C8FF');

    this.spinSpeed = params?.spinSpeed ?? 1; // revolutions per second
    this.inclinationAngle = THREE.MathUtils.degToRad(params?.inclinationAngle ?? 0);

    // Create sphere geometry with radius = STAR_RADIUS (1 scene unit)
    const widthSegments = geometryOptions?.widthSegments ?? 32;
    const heightSegments = geometryOptions?.heightSegments ?? 32;
    const geometry = new THREE.SphereGeometry(STAR_RADIUS, widthSegments, heightSegments);

    // Create emissive material with glow
    const color = params?.color ?? defaultColor;
    this.material = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.6,
      roughness: 0.3,
      metalness: 0.1,
    });

    this.mesh = new THREE.Mesh(geometry, this.material);

    // Compute spin axis from inclination angle
    this.spinAxis = new THREE.Vector3();
    this.updateSpinAxis();
  }

  /**
   * Updates the star rotation each frame.
   * Increments rotationPhase by spinSpeed * 2π * deltaTime and applies rotation quaternion.
   */
  update(deltaTime: number): void {
    // Increment rotation phase
    this.rotationPhase += this.spinSpeed * 2 * Math.PI * deltaTime;

    // Build the rotation quaternion around the tilted spin axis
    const axisQuaternion = new THREE.Quaternion();
    axisQuaternion.setFromAxisAngle(this.spinAxis, this.rotationPhase);

    // Apply the rotation to the mesh
    this.mesh.quaternion.copy(axisQuaternion);
  }

  /**
   * Sets the inclination angle (tilt from Y-axis around X-axis).
   * Preserves the current rotation phase.
   */
  setInclinationAngle(degrees: number): void {
    this.inclinationAngle = THREE.MathUtils.degToRad(degrees);
    this.updateSpinAxis();
  }

  /**
   * Sets the spin speed in revolutions per second.
   */
  setSpinSpeed(rps: number): void {
    this.spinSpeed = rps;
  }

  /**
   * Sets the material color while preserving emissive glow.
   * Both color and emissive are set to the provided color, emissive intensity remains > 0.
   */
  setColor(color: THREE.Color): void {
    this.material.color.copy(color);
    this.material.emissive.copy(color);
    // Ensure emissive intensity stays positive
    if (this.material.emissiveIntensity <= 0) {
      this.material.emissiveIntensity = 0.6;
    }
  }

  /**
   * Returns the current rotation phase in radians.
   */
  getRotationPhase(): number {
    return this.rotationPhase;
  }

  /**
   * Returns the current spin speed in revolutions per second.
   */
  getSpinSpeed(): number {
    return this.spinSpeed;
  }

  /**
   * Returns the current inclination angle in degrees.
   */
  getInclinationAngle(): number {
    return THREE.MathUtils.radToDeg(this.inclinationAngle);
  }

  /**
   * Computes the spin axis vector from the inclination angle.
   * The spin axis is the Y-axis tilted by the inclination angle around the X-axis.
   */
  private updateSpinAxis(): void {
    // Start from the Y-axis (0, 1, 0) and rotate around X-axis by the inclination angle
    this.spinAxis.set(0, 1, 0);
    const rotationQuat = new THREE.Quaternion();
    rotationQuat.setFromAxisAngle(new THREE.Vector3(1, 0, 0), this.inclinationAngle);
    this.spinAxis.applyQuaternion(rotationQuat);
    this.spinAxis.normalize();
  }
}
